import {
  BadgeCheck,
  Bell,
  CircleX,
  CirclePlay,
  ClipboardCheck,
  CopyCheck,
  FlaskConical,
  Gamepad2,
  Handshake,
  Image,
  LayoutGrid,
  Receipt,
  Scale,
  ScrollText,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Tag,
  TicketPercent,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';

/** Боковое меню: группы и пункты (порядок и подписи — по макету Main.dc.html). */
export interface NavigationItem {
  id: string;
  label: string;
  path: string;
  icon: LucideIcon;
  /** Доп. маршруты, при которых пункт активен (вкладки одного раздела). */
  match?: string[];
  /** Бейдж со счётчиком: пока только открытые возможные дубли. */
  badge?: 'suspectedDuplicates';
}

export interface NavigationGroup {
  id: string;
  /** Подпись группы; у «Главной» её нет. */
  label?: string;
  items: NavigationItem[];
}

export const navigationGroups: NavigationGroup[] = [
  {
    id: 'home',
    items: [{ id: 'dashboard', label: 'Главная', path: '/', icon: LayoutGrid }],
  },
  {
    id: 'users',
    label: 'Аккаунты',
    items: [
      { id: 'users', label: 'Пользователи', path: '/users', icon: Users },
      { id: 'duplicates', label: 'Дубли аккаунтов', path: '/duplicates', icon: CopyCheck, badge: 'suspectedDuplicates' },
      { id: 'security', label: 'Безопасность', path: '/security', icon: ShieldCheck },
    ],
  },
  {
    id: 'sales',
    label: 'Продажи',
    items: [
      { id: 'plans', label: 'Тарифы', path: '/plans', icon: Tag },
      { id: 'experiment', label: 'Эксперимент цен', path: '/experiment', icon: FlaskConical },
      { id: 'promoCodes', label: 'Промокоды', path: '/promo-codes', icon: TicketPercent },
      // Один пункт на два экрана: /orders (вкладка «Заказы») и /payments (вкладка «Платежи»).
      { id: 'ordersPayments', label: 'Заказы и платежи', path: '/orders', icon: Receipt, match: ['/payments'] },
      { id: 'subscriptions', label: 'Подписки', path: '/subscriptions', icon: BadgeCheck },
    ],
  },
  {
    id: 'content',
    label: 'Контент',
    items: [
      { id: 'notifications', label: 'Уведомления', path: '/notifications', icon: Bell },
      { id: 'banners', label: 'Баннеры', path: '/banners', icon: Image },
      { id: 'videoLessons', label: 'Видеоуроки', path: '/video-lessons', icon: CirclePlay },
      { id: 'games', label: 'Игры и лимиты', path: '/games', icon: Gamepad2 },
    ],
  },
  {
    id: 'learning',
    label: 'Обучение',
    items: [
      { id: 'examAttempts', label: 'Попытки экзаменов', path: '/exam-attempts', icon: ClipboardCheck },
      { id: 'wrongAnswers', label: 'Ошибки в ответах', path: '/wrong-answers', icon: CircleX },
    ],
  },
  {
    id: 'treasury',
    label: 'Казна',
    items: [
      { id: 'cashflow', label: 'Сверка поступлений', path: '/cashflow', icon: Scale },
      { id: 'expenses', label: 'Расходы', path: '/expenses', icon: Wallet },
      { id: 'founders', label: 'Основатели', path: '/founders', icon: Handshake },
    ],
  },
  {
    id: 'system',
    label: 'Система',
    items: [
      { id: 'appConfig', label: 'Конфиг приложения', path: '/app-config', icon: SlidersHorizontal },
      { id: 'settings', label: 'Настройки', path: '/settings', icon: Settings },
      { id: 'auditLog', label: 'Аудит-лог', path: '/audit-log', icon: ScrollText },
    ],
  },
];

/** Плоский список пунктов (поиск, тесты). */
export const navigationItems: NavigationItem[] = navigationGroups.flatMap((g) => g.items);

function matchesPath(pathname: string, path: string): boolean {
  if (path === '/') return pathname === '/';
  return pathname === path || pathname.startsWith(`${path}/`);
}

/** Пункт меню для текущего адреса: самый длинный совпавший путь (в т.ч. из `match`). */
export function findActiveNavItem(pathname: string): NavigationItem | undefined {
  let best: { item: NavigationItem; len: number } | undefined;
  for (const item of navigationItems) {
    for (const p of [item.path, ...(item.match ?? [])]) {
      if (matchesPath(pathname, p) && (!best || p.length > best.len)) best = { item, len: p.length };
    }
  }
  return best?.item;
}
